import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import { formatAmount } from '../format';

const { Text } = Typography;

/**
 * Every delivery from the AB machine: when, from which database, how much,
 * and whether debit equals credit.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @returns {JSX.Element} The deliveries table
 */
export default function AbSyncsTab({ companyId }) {
  const { t } = useTranslation('clientReports');
  const { data, isLoading } = useQuery({
    queryKey: ['client-reports-ab-syncs', companyId],
    queryFn: () => API.clientReports.getAbSyncs(companyId),
  });

  const columns = [
    {
      title: t('ab.received'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v, row) => (
        <>
          {dayjs(v).format('DD.MM.YYYY HH:mm')}{' '}
          {row.current && <Tag color="green">{t('ab.current')}</Tag>}
        </>
      ),
    },
    { title: t('ab.year'), dataIndex: 'year', key: 'year', width: 70 },
    {
      title: t('ab.source'),
      key: 'source',
      width: 120,
      render: (_, row) =>
        [row.abDatabase, row.abCompanyNo].filter(Boolean).join(' / '),
    },
    {
      title: t('ab.lastEntry'),
      dataIndex: 'lastEntryDate',
      key: 'lastEntryDate',
      width: 120,
      render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '—'),
    },
    {
      title: t('ab.lines'),
      dataIndex: 'lineCount',
      key: 'lineCount',
      width: 90,
      align: 'right',
    },
    {
      title: t('ab.accounts'),
      dataIndex: 'accountCount',
      key: 'accountCount',
      width: 80,
      align: 'right',
    },
    {
      title: t('ab.partners'),
      dataIndex: 'partnerCount',
      key: 'partnerCount',
      width: 80,
      align: 'right',
    },
    {
      title: t('ab.debit'),
      dataIndex: 'debitTotal',
      key: 'debitTotal',
      align: 'right',
      render: formatAmount,
    },
    {
      title: t('ab.credit'),
      dataIndex: 'creditTotal',
      key: 'creditTotal',
      align: 'right',
      render: (v, row) => (
        <Text
          type={Math.abs(v - row.debitTotal) >= 0.01 ? 'danger' : undefined}
        >
          {formatAmount(v)}
        </Text>
      ),
    },
    {
      title: t('ab.notes'),
      dataIndex: 'notes',
      key: 'notes',
      render: (notes) =>
        (notes || []).map((n) => (
          <div key={n} style={{ fontSize: 12 }}>
            {n}
          </div>
        )),
    },
  ];

  return (
    <Table
      rowKey="_id"
      size="small"
      loading={isLoading}
      columns={columns}
      dataSource={data?.data || []}
      pagination={{ pageSize: 30 }}
      scroll={{ x: true }}
    />
  );
}
